import {
    type ViewStyle,
    Platform
} from "react-native";
import {
    CAPSULE_THRESHOLD
} from "./concentric";
import {
    webStyle
} from "../../utils";

export type CornerStyleConstantType = {
    shapeConfigs: Pick<NCoreUIKit.ShapeConfigs, "cornerShape" | "squircleRadiusScale">;
    radius: number;
};

export type CornerKeyType = "borderStartStartRadius" | "borderStartEndRadius" | "borderEndStartRadius" | "borderEndEndRadius";

export type CornerRadiiType = Partial<Record<CornerKeyType, number>>;

export type CornersStyleConstantType = {
    shapeConfigs: Pick<NCoreUIKit.ShapeConfigs, "cornerShape" | "squircleRadiusScale">;
    corners: CornerRadiiType;
};

const MINIMUM_SQUIRCLE_RADIUS = 4;

let isCornerShapeSupportedCache: boolean | null = null;

export const getIsCornerShapeSupported = (): boolean => {
    if(Platform.OS !== "web") {
        return false;
    }

    if(isCornerShapeSupportedCache === null) {
        isCornerShapeSupportedCache = typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("corner-shape", "squircle");
    }

    return isCornerShapeSupportedCache;
};

export const getCornerStyle = ({
    shapeConfigs,
    radius
}: CornerStyleConstantType): ViewStyle => {
    if(shapeConfigs.cornerShape !== "squircle" || radius < MINIMUM_SQUIRCLE_RADIUS || radius >= CAPSULE_THRESHOLD) {
        return {
            borderRadius: radius
        };
    }

    if(Platform.OS === "web") {
        if(getIsCornerShapeSupported()) {
            return {
                borderRadius: radius * shapeConfigs.squircleRadiusScale,
                ...webStyle({
                    cornerShape: "squircle"
                })
            };
        }

        return {
            borderRadius: radius
        };
    }

    if(Platform.OS === "ios") {
        return {
            borderCurve: "continuous",
            borderRadius: radius
        };
    }

    return {
        borderRadius: radius
    };
};

export const getCornersStyle = ({
    shapeConfigs,
    corners
}: CornersStyleConstantType): ViewStyle => {
    const radii = Object.values(corners).filter((radius): radius is number => typeof radius === "number");

    const maxRadius = radii.length ? Math.max(...radii) : 0;

    if(shapeConfigs.cornerShape !== "squircle" || maxRadius < MINIMUM_SQUIRCLE_RADIUS || maxRadius >= CAPSULE_THRESHOLD) {
        return {
            ...corners
        };
    }

    if(Platform.OS === "web") {
        if(!getIsCornerShapeSupported()) {
            return {
                ...corners
            };
        }

        const scaledCorners: CornerRadiiType = {};

        (Object.keys(corners) as Array<CornerKeyType>).forEach((key) => {
            const radius = corners[key];

            if(radius !== undefined) {
                scaledCorners[key] = radius * shapeConfigs.squircleRadiusScale;
            }
        });

        return {
            ...scaledCorners,
            ...webStyle({
                cornerShape: "squircle"
            })
        };
    }

    if(Platform.OS === "ios") {
        return {
            borderCurve: "continuous",
            ...corners
        };
    }

    return {
        ...corners
    };
};
